import { projectsProjectOrderReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-order-reference';
import { z } from 'zod';
import { procurementOperationCounterpartySchema, procurementOperationCurrencyCodeSchema, procurementOperationIdSchema, procurementOperationMoneyCentsSchema, procurementOperationPageSchema, procurementOperationQuantityTenThousandthsSchema, procurementOperationReasonSchema, procurementOperationSignedMoneyCentsSchema, procurementOperationSourceReferenceSchema, procurementOperationStateCommandSchema, } from './procurement-operation-common.contract.js';
export const procurementFulfillmentCapability = 'procurement.fulfillment.v1';
export const procurementFulfillmentStatusSchema = z.enum([
    'draft', 'quoted', 'ordered', 'partial', 'complete', 'cancelled', 'void',
]);
export const procurementFulfillmentDeliveryStatusSchema = z.enum([
    'not_scheduled', 'scheduled', 'in_transit', 'delivered', 'delayed', 'cancelled',
]);
export const procurementFulfillmentPickupStatusSchema = z.enum([
    'not_scheduled', 'scheduled', 'picked_up', 'delayed', 'cancelled',
]);
export const procurementFulfillmentEventTypeSchema = z.enum([
    'order_confirmed', 'delivery_scheduled', 'delivery_update', 'delivered', 'shortage',
    'backorder', 'substitution', 'price_change', 'pickup_ready', 'pickup_completed', 'note',
    'cancelled',
]);
export const procurementFulfillmentEventStatusSchema = z.enum(['open', 'monitoring', 'resolved', 'closed']);
export const procurementFulfillmentEventSeveritySchema = z.enum(['info', 'warning', 'critical']);
export const procurementFulfillmentLineSchema = z.strictObject({
    sourceReference: procurementOperationSourceReferenceSchema,
    name: z.string().trim().min(1).max(240),
    description: z.string().trim().max(2_000).nullable(),
    quantityTenThousandths: procurementOperationQuantityTenThousandthsSchema.min(1),
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: procurementOperationMoneyCentsSchema,
    lineTotalCents: procurementOperationMoneyCentsSchema,
});
const procurementFulfillmentDraftBaseSchema = z.strictObject({
    projectId: z.string().uuid(),
    purchaseOrderId: z.string().uuid(),
    supplier: procurementOperationCounterpartySchema,
    supplierBranch: z.string().trim().max(240).nullable(),
    supplierOrderReference: z.string().trim().max(240).nullable(),
    status: procurementFulfillmentStatusSchema,
    deliveryStatus: procurementFulfillmentDeliveryStatusSchema,
    pickupStatus: procurementFulfillmentPickupStatusSchema,
    requestedDeliveryDate: z.string().date().nullable(),
    scheduledDeliveryAt: z.string().datetime({ offset: true }).nullable(),
    actualDeliveryAt: z.string().datetime({ offset: true }).nullable(),
    currencyCode: procurementOperationCurrencyCodeSchema,
    subtotalCents: procurementOperationMoneyCentsSchema,
    taxCents: procurementOperationMoneyCentsSchema,
    totalCents: procurementOperationMoneyCentsSchema,
    actualSubtotalCents: procurementOperationMoneyCentsSchema.nullable(),
    varianceCents: procurementOperationSignedMoneyCentsSchema.nullable(),
    lines: z.array(procurementFulfillmentLineSchema).min(1).max(250),
    notes: z.string().trim().max(8_000).nullable(),
});
function validateFulfillmentCosts(draft, context) {
    if (draft.totalCents === null || draft.subtotalCents === null || draft.taxCents === null)
        return;
    if (draft.totalCents !== draft.subtotalCents + draft.taxCents) {
        context.addIssue({ code: 'custom', path: ['totalCents'], message: 'Fulfillment total must equal subtotal plus tax.' });
    }
    const calculated = draft.lines.reduce((sum, line) => sum + (line.lineTotalCents ?? 0), 0);
    if (draft.lines.some((line) => line.lineTotalCents === null))
        return;
    if (calculated !== draft.subtotalCents) {
        context.addIssue({ code: 'custom', path: ['subtotalCents'], message: 'Fulfillment lines must reconcile to subtotal.' });
    }
    if ((draft.actualSubtotalCents === null) !== (draft.varianceCents === null)) {
        context.addIssue({ code: 'custom', path: ['actualSubtotalCents'], message: 'Actual subtotal and variance must be present together.' });
    }
    if (draft.actualSubtotalCents !== null && draft.varianceCents !== draft.actualSubtotalCents - draft.subtotalCents) {
        context.addIssue({ code: 'custom', path: ['varianceCents'], message: 'Fulfillment variance must reconcile exactly.' });
    }
}
export const procurementFulfillmentDraftSchema = procurementFulfillmentDraftBaseSchema.superRefine(validateFulfillmentCosts);
const procurementFulfillmentEventInputBaseSchema = z.strictObject({
    eventId: z.string().uuid(),
    eventKey: z.string().trim().min(1).max(240),
    eventType: procurementFulfillmentEventTypeSchema,
    eventStatus: procurementFulfillmentEventStatusSchema,
    severity: procurementFulfillmentEventSeveritySchema,
    occurredAt: z.string().datetime({ offset: true }),
    expectedResolutionAt: z.string().datetime({ offset: true }).nullable(),
    resolvedAt: z.string().datetime({ offset: true }).nullable(),
    quantityAffectedTenThousandths: procurementOperationQuantityTenThousandthsSchema.nullable(),
    affectedItems: z.array(z.strictObject({
        lineNumber: z.number().int().positive().nullable(),
        name: z.string().trim().min(1).max(240),
        quantityTenThousandths: procurementOperationQuantityTenThousandthsSchema,
        unit: z.string().trim().min(1).max(60),
        note: z.string().trim().max(1_000).nullable(),
    })).max(250),
    sourceReference: procurementOperationSourceReferenceSchema,
    notes: z.string().trim().max(8_000).nullable(),
});
function validateFulfillmentEvent(event, context) {
    if (event.eventStatus === 'resolved' && event.resolvedAt === null) {
        context.addIssue({ code: 'custom', path: ['resolvedAt'], message: 'Resolved fulfillment events require a resolution time.' });
    }
}
export const procurementFulfillmentEventInputSchema = procurementFulfillmentEventInputBaseSchema.superRefine(validateFulfillmentEvent);
export const procurementFulfillmentCreateCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    fulfillmentId: procurementOperationIdSchema,
    fulfillment: procurementFulfillmentDraftSchema,
    reason: procurementOperationReasonSchema,
});
export const procurementFulfillmentReviseCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    fulfillment: procurementFulfillmentDraftSchema,
    reason: procurementOperationReasonSchema,
});
export const procurementFulfillmentEventCommandSchema = z.strictObject({
    kind: z.literal('record-event'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    event: procurementFulfillmentEventInputSchema,
    reason: procurementOperationReasonSchema,
});
export const procurementFulfillmentCommandSchema = z.discriminatedUnion('kind', [
    procurementFulfillmentReviseCommandSchema,
    procurementFulfillmentEventCommandSchema,
    procurementOperationStateCommandSchema,
]);
const fulfillmentPublicDataSchema = procurementFulfillmentDraftBaseSchema.omit({ projectId: true }).extend({
    projectReference: projectsProjectOrderReferenceSnapshotSchema,
    subtotalCents: procurementOperationMoneyCentsSchema.nullable(),
    taxCents: procurementOperationMoneyCentsSchema.nullable(),
    totalCents: procurementOperationMoneyCentsSchema.nullable(),
    actualSubtotalCents: procurementOperationMoneyCentsSchema.nullable(),
    varianceCents: procurementOperationSignedMoneyCentsSchema.nullable(),
    lines: z.array(procurementFulfillmentLineSchema.extend({
        unitPriceCents: procurementOperationMoneyCentsSchema.nullable(),
        lineTotalCents: procurementOperationMoneyCentsSchema.nullable(),
    })),
}).superRefine(validateFulfillmentCosts);
export const procurementFulfillmentEventSchema = procurementFulfillmentEventInputBaseSchema.extend({
    contractVersion: z.literal(procurementFulfillmentCapability),
    tenantId: z.string().uuid(),
    fulfillmentId: z.string().uuid(),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
}).superRefine(validateFulfillmentEvent);
export const procurementFulfillmentSchema = z.strictObject({
    contractVersion: z.literal(procurementFulfillmentCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    costsVisible: z.boolean(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    data: fulfillmentPublicDataSchema,
    events: z.array(procurementFulfillmentEventSchema),
});
export const procurementFulfillmentListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    projectId: z.string().uuid().nullable().default(null),
    status: procurementFulfillmentStatusSchema.nullable().default(null),
    deliveryStatus: procurementFulfillmentDeliveryStatusSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const procurementFulfillmentResponseSchema = z.strictObject({
    data: procurementFulfillmentSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const procurementFulfillmentsResponseSchema = z.strictObject({
    data: z.array(procurementFulfillmentSchema),
    page: procurementOperationPageSchema,
});
export function calculateProcurementFulfillmentLineTotal(quantityTenThousandths, unitPriceCents) {
    const quantity = procurementOperationQuantityTenThousandthsSchema.min(1).parse(quantityTenThousandths);
    const price = procurementOperationMoneyCentsSchema.parse(unitPriceCents);
    return procurementOperationMoneyCentsSchema.parse(Number((BigInt(quantity) * BigInt(price) + 5000n) / 10000n));
}
//# sourceMappingURL=procurement-fulfillment.contract.js.map